import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SlidersIcon, XIcon } from 'lucide-react';
import SearchBar from '../components/SearchBar';
import PropertyCard from '../components/PropertyCard';
import { allProperties } from '../utils/mockData';
const SearchResultsPage = () => {
  const [searchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  // Filter states
  const [priceRange, setPriceRange] = useState([0, 1000]);
  const [selectedPropertyTypes, setSelectedPropertyTypes] = useState([]);
  const [selectedAmenities, setSelectedAmenities] = useState([]);
  const [selectedRating, setSelectedRating] = useState(0);
  const destination = searchParams.get('destination') || '';
  const checkIn = searchParams.get('checkIn') || '';
  const checkOut = searchParams.get('checkOut') || '';
  const guests = parseInt(searchParams.get('guests') || '1');
  // Simulate API call with the search parameters
  useEffect(() => {
    // In a real app, this would be an API call with the search parameters
    const fetchProperties = () => {
      // Filter properties based on destination (case insensitive)
      let filtered = [...allProperties];
      if (destination) {
        filtered = filtered.filter(property => property.location.toLowerCase().includes(destination.toLowerCase()));
      }
      // More filters would be applied here in a real app
      // For example, checking availability for the selected dates
      setProperties(filtered);
      setFilteredProperties(filtered);
    };
    fetchProperties();
  }, [destination, checkIn, checkOut, guests]);
  // Apply filters
  const applyFilters = () => {
    let filtered = [...properties];
    // Apply price filter
    filtered = filtered.filter(property => property.pricePerNight >= priceRange[0] && property.pricePerNight <= priceRange[1]);
    // Apply property type filter
    if (selectedPropertyTypes.length > 0) {
      filtered = filtered.filter(property => selectedPropertyTypes.includes(property.propertyType));
    }
    // Apply amenities filter
    if (selectedAmenities.length > 0) {
      filtered = filtered.filter(property => selectedAmenities.every(amenity => property.amenities.includes(amenity)));
    }
    // Apply rating filter
    if (selectedRating > 0) {
      filtered = filtered.filter(property => property.rating >= selectedRating);
    }
    setFilteredProperties(filtered);
    // Close filter on mobile after applying
    if (window.innerWidth < 768) {
      setIsFilterOpen(false);
    }
  };
  // Reset filters
  const resetFilters = () => {
    setPriceRange([0, 1000]);
    setSelectedPropertyTypes([]);
    setSelectedAmenities([]);
    setSelectedRating(0);
    setFilteredProperties(properties);
  };
  // Toggle property type selection
  const togglePropertyType = type => {
    if (selectedPropertyTypes.includes(type)) {
      setSelectedPropertyTypes(selectedPropertyTypes.filter(t => t !== type));
    } else {
      setSelectedPropertyTypes([...selectedPropertyTypes, type]);
    }
  };
  // Toggle amenity selection
  const toggleAmenity = amenity => {
    if (selectedAmenities.includes(amenity)) {
      setSelectedAmenities(selectedAmenities.filter(a => a !== amenity));
    } else {
      setSelectedAmenities([...selectedAmenities, amenity]);
    }
  };
  // Get unique property types from the data
  const propertyTypes = ['bnb', 'farmstay'];
  // Common amenities
  const amenities = ['Wi-Fi', 'Kitchen', 'Pool', 'Free parking', 'Air conditioning', 'Washer', 'TV', 'Hot tub', 'Gym', 'Breakfast'];
  return <div className="bg-[#F8F5F0] min-h-screen w-full" data-id="element-1109">
      <div className="container mx-auto px-4 py-6" data-id="element-1110">
        <div className="bg-white rounded-lg shadow-md p-4 mb-6" data-id="element-1111">
          <SearchBar data-id="element-1112" />
        </div>
        <div className="flex flex-col md:flex-row gap-6" data-id="element-1113">
          {/* Filters - Desktop */}
          <div className="hidden md:block w-64 bg-white rounded-lg shadow-md p-4 h-fit" data-id="element-1114">
            <div className="flex justify-between items-center mb-4" data-id="element-1115">
              <h3 className="font-semibold text-lg" data-id="element-1116">Filters</h3>
              <button onClick={resetFilters} className="text-[#006D77] text-sm hover:underline" data-id="element-1117">
                Reset all
              </button>
            </div>
            {/* Price Range */}
            <div className="mb-6" data-id="element-1118">
              <h4 className="font-medium mb-3" data-id="element-1119">Price range</h4>
              <div className="flex justify-between mb-2" data-id="element-1120">
                <span data-id="element-1121">${priceRange[0]}</span>
                <span data-id="element-1122">${priceRange[1]}</span>
              </div>
              <input type="range" min="0" max="1000" value={priceRange[1]} onChange={e => setPriceRange([priceRange[0], parseInt(e.target.value)])} className="w-full" data-id="element-1123" />
            </div>
            {/* Property Type */}
            <div className="mb-6" data-id="element-1124">
              <h4 className="font-medium mb-3" data-id="element-1125">Property type</h4>
              <div className="space-y-2" data-id="element-1126">
                <label className="flex items-center" data-id="element-1127">
                  <input type="checkbox" checked={selectedPropertyTypes.includes('bnb')} onChange={() => togglePropertyType('bnb')} className="rounded text-[#D4A017] focus:ring-[#D4A017] mr-2" data-id="element-1128" />
                  <span className="capitalize" data-id="element-1129">Bed & Breakfast</span>
                </label>
                <label className="flex items-center" data-id="element-1130">
                  <input type="checkbox" checked={selectedPropertyTypes.includes('farmstay')} onChange={() => togglePropertyType('farmstay')} className="rounded text-[#D4A017] focus:ring-[#D4A017] mr-2" data-id="element-1131" />
                  <span className="capitalize" data-id="element-1132">Farmstay</span>
                </label>
              </div>
            </div>
            {/* Amenities */}
            <div className="mb-6" data-id="element-1133">
              <h4 className="font-medium mb-3" data-id="element-1134">Amenities</h4>
              <div className="space-y-2" data-id="element-1135">
                {amenities.map(amenity => <label key={amenity} className="flex items-center" data-id="element-1136">
                    <input type="checkbox" checked={selectedAmenities.includes(amenity)} onChange={() => toggleAmenity(amenity)} className="rounded text-[#D4A017] focus:ring-[#D4A017] mr-2" data-id="element-1137" />
                    <span data-id="element-1138">{amenity}</span>
                  </label>)}
              </div>
            </div>
            {/* Rating */}
            <div className="mb-6" data-id="element-1139">
              <h4 className="font-medium mb-3" data-id="element-1140">Rating</h4>
              <div className="flex space-x-2" data-id="element-1141">
                {[1, 2, 3, 4, 5].map(rating => <button key={rating} onClick={() => setSelectedRating(rating)} className={`w-8 h-8 rounded-full flex items-center justify-center ${selectedRating >= rating ? 'bg-[#D4A017] text-white' : 'bg-gray-100'}`} data-id="element-1142">
                    {rating}
                  </button>)}
              </div>
            </div>
            <button onClick={applyFilters} className="w-full bg-[#006D77] hover:bg-[#00595f] text-white py-2 rounded-md font-medium" data-id="element-1143">
              Apply Filters
            </button>
          </div>
          {/* Filter Button - Mobile */}
          <div className="md:hidden mb-4" data-id="element-1144">
            <button onClick={() => setIsFilterOpen(true)} className="flex items-center bg-white px-4 py-2 rounded-md shadow-sm" data-id="element-1145">
              <SlidersIcon className="h-5 w-5 mr-2" data-id="element-1146" />
              Filters
            </button>
          </div>
          {/* Filter Modal - Mobile */}
          {isFilterOpen && <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-end" data-id="element-1147">
              <div className="bg-white w-4/5 h-full overflow-y-auto p-4" data-id="element-1148">
                <div className="flex justify-between items-center mb-4" data-id="element-1149">
                  <h3 className="font-semibold text-lg" data-id="element-1150">Filters</h3>
                  <button onClick={() => setIsFilterOpen(false)} data-id="element-1151">
                    <XIcon className="h-6 w-6" data-id="element-1152" />
                  </button>
                </div>
                {/* Mobile filters - same as desktop */}
                {/* Price Range */}
                <div className="mb-6" data-id="element-1153">
                  <h4 className="font-medium mb-3" data-id="element-1154">Price range</h4>
                  <div className="flex justify-between mb-2" data-id="element-1155">
                    <span data-id="element-1156">${priceRange[0]}</span>
                    <span data-id="element-1157">${priceRange[1]}</span>
                  </div>
                  <input type="range" min="0" max="1000" value={priceRange[1]} onChange={e => setPriceRange([priceRange[0], parseInt(e.target.value)])} className="w-full" data-id="element-1158" />
                </div>
                {/* Property Type */}
                <div className="mb-6" data-id="element-1159">
                  <h4 className="font-medium mb-3" data-id="element-1160">Property type</h4>
                  <div className="space-y-2" data-id="element-1161">
                    <label className="flex items-center" data-id="element-1162">
                      <input type="checkbox" checked={selectedPropertyTypes.includes('bnb')} onChange={() => togglePropertyType('bnb')} className="rounded text-[#D4A017] focus:ring-[#D4A017] mr-2" data-id="element-1163" />
                      <span className="capitalize" data-id="element-1164">Bed & Breakfast</span>
                    </label>
                    <label className="flex items-center" data-id="element-1165">
                      <input type="checkbox" checked={selectedPropertyTypes.includes('farmstay')} onChange={() => togglePropertyType('farmstay')} className="rounded text-[#D4A017] focus:ring-[#D4A017] mr-2" data-id="element-1166" />
                      <span className="capitalize" data-id="element-1167">Farmstay</span>
                    </label>
                  </div>
                </div>
                {/* Amenities */}
                <div className="mb-6" data-id="element-1168">
                  <h4 className="font-medium mb-3" data-id="element-1169">Amenities</h4>
                  <div className="space-y-2" data-id="element-1170">
                    {amenities.map(amenity => <label key={amenity} className="flex items-center" data-id="element-1171">
                        <input type="checkbox" checked={selectedAmenities.includes(amenity)} onChange={() => toggleAmenity(amenity)} className="rounded text-[#D4A017] focus:ring-[#D4A017] mr-2" data-id="element-1172" />
                        <span data-id="element-1173">{amenity}</span>
                      </label>)}
                  </div>
                </div>
                {/* Rating */}
                <div className="mb-6" data-id="element-1174">
                  <h4 className="font-medium mb-3" data-id="element-1175">Rating</h4>
                  <div className="flex space-x-2" data-id="element-1176">
                    {[1, 2, 3, 4, 5].map(rating => <button key={rating} onClick={() => setSelectedRating(rating)} className={`w-8 h-8 rounded-full flex items-center justify-center ${selectedRating >= rating ? 'bg-[#D4A017] text-white' : 'bg-gray-100'}`} data-id="element-1177">
                        {rating}
                      </button>)}
                  </div>
                </div>
                <div className="flex space-x-3" data-id="element-1178">
                  <button onClick={resetFilters} className="flex-1 border border-gray-300 py-2 rounded-md font-medium" data-id="element-1179">
                    Reset
                  </button>
                  <button onClick={applyFilters} className="flex-1 bg-[#006D77] hover:bg-[#00595f] text-white py-2 rounded-md font-medium" data-id="element-1180">
                    Apply
                  </button>
                </div>
              </div>
            </div>}
          {/* Search Results */}
          <div className="flex-1" data-id="element-1181">
            <div className="mb-4" data-id="element-1182">
              <h2 className="text-2xl font-bold text-[#333333]" data-id="element-1183">
                {filteredProperties.length}{' '}
                {filteredProperties.length === 1 ? 'property' : 'properties'}{' '}
                found
                {destination ? ` in ${destination}` : ''}
              </h2>
              {checkIn && checkOut && <p className="text-gray-600" data-id="element-1184">
                  {new Date(checkIn).toLocaleDateString()} -{' '}
                  {new Date(checkOut).toLocaleDateString()} · {guests}{' '}
                  {guests === 1 ? 'guest' : 'guests'}
                </p>}
            </div>
            {filteredProperties.length > 0 ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1185">
                {filteredProperties.map(property => <PropertyCard key={property.id} property={property} data-id="element-1186" />)}
              </div> : <div className="bg-white rounded-lg shadow-md p-8 text-center" data-id="element-1187">
                <h3 className="text-xl font-semibold mb-2" data-id="element-1188">
                  No properties found
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-1189">
                  Try adjusting your filters or search for a different location.
                </p>
                <button onClick={resetFilters} className="bg-[#006D77] hover:bg-[#00595f] text-white px-6 py-2 rounded-md font-medium" data-id="element-1190">
                  Reset Filters
                </button>
              </div>}
          </div>
        </div>
      </div>
    </div>;
};
export default SearchResultsPage;